// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environment';

/**
 * Key contact engagement from employee data
 */
export interface KeyContactEngagement {
  description: string;
  engagementTypeId: string;
  projectId: string;
}

/**
 * Employee with key contact role
 */
export interface KeyContactEmployeeDto {
  name: string;
  email: string;
  userId: string;
  avatarUrl: string;
  engagementType: KeyContactEngagement[];
}

/**
 * A group of key contacts by role type
 */
export interface KeyContactRoleGroup {
  type: string;
  label: string;
  employees: KeyContactEmployeeDto[];
}

/**
 * Response for GET key contacts endpoint
 */
export interface GetKeyContactsResponse {
  contacts: KeyContactRoleGroup[];
}

/**
 * Request payload for ADD key contact endpoint
 */
export interface AddKeyContactRequest {
  firstName: string;
  lastName: string;
  email: string;
  membershipId: string;
  roleName: string;
}

/**
 * Result of a single contact operation (add or remove)
 */
export interface ContactOperationResult {
  type: 'add' | 'remove';
  email: string;
  name: string;
  success: boolean;
  error?: string;
}

/**
 * KeyContactsRestService
 *
 * Frontend service that calls the REST endpoints for key contacts.
 * All business logic has been moved to the backend.
 * This service is responsible only for HTTP communication.
 *
 * Endpoints:
 * - GET /orgs/:orgId/projects/:projectId/key-contacts
 * - POST /orgs/:orgId/projects/:projectId/key-contacts
 * - DELETE /orgs/:orgId/projects/:projectId/key-contacts/:roleId
 */
@Injectable({
  providedIn: 'root',
})
export class KeyContactsRestService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}`;

  /**
   * Get key contacts for a project, grouped by role type.
   * All data transformations are done server-side.
   *
   * @param orgId - Organization ID (Salesforce B2B Account ID)
   * @param projectId - Project/Foundation ID
   * @param isAllOrgs - Whether to include all organizations in rollup
   * @param bustCache - Whether to add cache-busting parameter (for refresh after updates)
   */
  getKeyContacts(
    orgId: string,
    projectId: string,
    isAllOrgs = false,
    bustCache = false
  ): Observable<GetKeyContactsResponse> {
    let params = new HttpParams().set('isAllOrgs', isAllOrgs.toString());

    if (bustCache) {
      params = params.set('_t', Date.now().toString());
    }

    return this.http.get<GetKeyContactsResponse>(
      `${this.baseUrl}/orgs/${orgId}/projects/${projectId}/key-contacts`,
      { params }
    );
  }

  /**
   * Add a new key contact.
   * Returns individual operation result for partial failure handling.
   *
   * @param orgId - Organization ID
   * @param projectId - Project/Foundation ID
   * @param request - Contact details
   */
  addKeyContact(
    orgId: string,
    projectId: string,
    request: AddKeyContactRequest
  ): Observable<ContactOperationResult> {
    return this.http.post<ContactOperationResult>(
      `${this.baseUrl}/orgs/${orgId}/projects/${projectId}/key-contacts`,
      request
    );
  }

  /**
   * Remove a key contact.
   * Returns individual operation result for partial failure handling.
   *
   * @param orgId - Organization ID
   * @param projectId - Project/Foundation ID
   * @param roleId - The role ID to remove (from engagementTypeId)
   * @param email - Contact email (for response identification)
   * @param name - Contact name (for response identification)
   */
  removeKeyContact(
    orgId: string,
    projectId: string,
    roleId: string,
    email: string,
    name: string
  ): Observable<ContactOperationResult> {
    const params = new HttpParams()
      .set('email', email)
      .set('name', name);

    return this.http.delete<ContactOperationResult>(
      `${this.baseUrl}/orgs/${orgId}/projects/${projectId}/key-contacts/${roleId}`,
      { params }
    );
  }
}
